<template>
  <div class="landing-page min-h-screen">
    <!-- Floating background images -->
    <div class="floating-images">
      <img
        v-for="(image, index) in floatingImages"
        :key="index"
        :src="image.src"
        :alt="image.name"
        class="floating-image"
        :style="{
          left: image.x,
          top: image.y,
          width: image.size,
          animationName: image.animationName,
          animationDelay: image.delay,
          animationDuration: image.duration
        }"
      />
    </div>

    <!-- Main container with max-width constraint -->
    <div class="max-w-md mx-auto px-2 py-2 flex flex-col gap-2">
      <!-- Первые 2 секции - видны сразу -->
      <LandingHeader />
      <GiftBoxApp class="landing-card"/>

      <!-- Секции с анимацией появления -->
      <div ref="section1" class="section-animated">
        <FeaturesSection />
      </div>
      <div ref="section5" class="section-animated">
        <SecuritySection />
      </div>

      <div ref="section2" class="section-animated">
        <UseCasesSection />
      </div>

      <div ref="section3" class="section-animated">
        <HowItWorksSection />
      </div>

      

      

      <div ref="section6" class="section-animated">
        <FaqSection />
      </div>

      <div ref="section7" class="section-animated">
        <HeroSection />
      </div>
      <div ref="section4" class="section-animated">
        <TechnicalSpecsSection />
      </div>

      <LandingFooter />
    </div>

    <!-- GiftBox Modal -->
    <GiftBoxModal :is-open="isModalOpen" @close="closeGiftBoxModal" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, provide } from 'vue'
import GiftBoxModal from '../components/GiftBoxModal.vue'
import GiftBoxApp from '../components/GiftBoxApp.vue'
import LandingHeader from '../components/landing/LandingHeader.vue'
import HeroSection from '../components/landing/HeroSection.vue'
import FeaturesSection from '../components/landing/FeaturesSection.vue'
import UseCasesSection from '../components/landing/UseCasesSection.vue'
import HowItWorksSection from '../components/landing/HowItWorksSection.vue'
import TechnicalSpecsSection from '../components/landing/TechnicalSpecsSection.vue'
import SecuritySection from '../components/landing/SecuritySection.vue'
import FaqSection from '../components/landing/FaqSection.vue'
import LandingFooter from '../components/landing/LandingFooter.vue'

const section1 = ref<HTMLElement | null>(null)
const section2 = ref<HTMLElement | null>(null)
const section3 = ref<HTMLElement | null>(null)
const section4 = ref<HTMLElement | null>(null)
const section5 = ref<HTMLElement | null>(null)
const section6 = ref<HTMLElement | null>(null)
const section7 = ref<HTMLElement | null>(null)

let observer: IntersectionObserver | null = null

// Modal state
const isModalOpen = ref(false)

const openGiftBoxModal = () => {
  isModalOpen.value = true
}

const closeGiftBoxModal = () => {
  isModalOpen.value = false
}

// Provide modal controls to child components
provide('openGiftBoxModal', openGiftBoxModal)

// Available images for floating animation
const availableImages = [
  { name: 'cake', src: '/landing/cake.png' },
  { name: 'cat', src: '/landing/cat.png' },
  { name: 'cucumber', src: '/landing/cucumber.png' },
  { name: 'cucumber2', src: '/landing/cucumber2.png' },
  { name: 'duck', src: '/landing/duck.png' },
  { name: 'snowman', src: '/landing/snowman.png' },
  { name: 'xmas', src: '/landing/xmas.png' }
]

// Show all 7 images with different positions and speeds
// Position images on the sides, avoiding center area
const floatingImages = computed(() => {
  return availableImages.map((img, index) => {
    // Alternate between left and right sides
    const isLeft = index % 2 === 0
    const seed = index * 0.618 // Golden ratio for distribution
    const x = isLeft
      ? `${(seed % 1) * 25}%`  // Left side: 0-25%
      : `${75 + (seed % 1) * 25}%`  // Right side: 75-100%

    const y = `${5 + (index * 12) + ((seed * 10) % 1) * 8}%`

    // Different animation for each image
    const animationName = `float${(index % 3) + 1}`

    return {
      ...img,
      x,
      y,
      size: `${120 + ((seed * 100) % 1) * 100}px`,  // Larger: 120-220px
      delay: `${index * 0.8}s`,
      duration: `${20 + ((seed * 10) % 1) * 15}s`,
      animationName
    }
  })
})

onMounted(() => {
  // Intersection Observer для отслеживания появления/исчезновения секций
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Элемент попал в viewport - показываем
          entry.target.classList.add('is-visible')
        } else {
          // Элемент ушел из viewport - скрываем
          entry.target.classList.remove('is-visible')
        }
      })
    },
    {
      threshold: 0.15, // Триггерится когда 15% элемента видно
      rootMargin: '0px 0px -100px 0px' // Отступ для более плавного появления
    }
  )

  // Наблюдаем за всеми секциями
  const sections = [section1, section2, section3, section4, section5, section6, section7]
  sections.forEach((section) => {
    if (section.value) {
      observer?.observe(section.value)
    }
  })
})

onUnmounted(() => {
  observer?.disconnect()
})
</script>

<style scoped>
.landing-page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: relative;
  overflow-x: hidden;
}

/* Floating images container */
.floating-images {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

/* Individual floating image */
.floating-image {
  position: absolute;
  opacity: 1;
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;
  will-change: transform;
}

/* Keyframes for floating animations - large circular paths */
@keyframes float1 {
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  25% {
    transform: translate(120px, -80px) rotate(15deg);
  }
  50% {
    transform: translate(60px, -160px) rotate(-10deg);
  }
  75% {
    transform: translate(-80px, -100px) rotate(20deg);
  }
}

@keyframes float2 {
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  20% {
    transform: translate(-100px, 60px) rotate(-12deg);
  }
  40% {
    transform: translate(-140px, -40px) rotate(18deg);
  }
  60% {
    transform: translate(-60px, -140px) rotate(-8deg);
  }
  80% {
    transform: translate(80px, -80px) rotate(25deg);
  }
}

@keyframes float3 {
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  15% {
    transform: translate(90px, 100px) rotate(10deg);
  }
  35% {
    transform: translate(150px, -20px) rotate(-15deg);
  }
  55% {
    transform: translate(40px, -120px) rotate(22deg);
  }
  75% {
    transform: translate(-70px, -60px) rotate(-18deg);
  }
  90% {
    transform: translate(-40px, 80px) rotate(12deg);
  }
}

/* Main content wrapper - above floating images */
.max-w-md {
  position: relative;
  z-index: 1;
  overflow-x: hidden;
}

/* Единый стиль для всех анимированных секций */
.section-animated {
  opacity: 0.7;
  transform: translateY(15px);
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: transform, opacity;
}

.section-animated.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Для плавной работы на мобильных */
@media (prefers-reduced-motion: reduce) {
  .section-animated {
    opacity: 1;
    transform: none;
    transition: none;
  }

  .floating-image {
    animation: none;
    opacity: 1;
  }
}
</style>
